import React, { useEffect, useState, useRef, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ExternalLink, LifeBuoy, LockKeyhole, ShieldCheck, X } from 'lucide-react';
interface PortfolioGateProps {
  isOpen: boolean;
  onClose: () => void;
  onUnlocked: () => void;
}
// Demo-only front-end password. This is not secure authentication and must not
// be used to protect genuinely confidential work.
export const PORTFOLIO_ACCESS_PASSWORD = 'byteweb-client';
export const PORTFOLIO_ACCESS_SESSION_KEY = 'byteweb-portfolio-access';
export const SUPPORT_PORTAL_URL = 'https://portal.byteweb.co.za/';
export function PortfolioGate({
  isOpen,
  onClose,
  onUnlocked
}: PortfolioGateProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const portalLinkRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => portalLinkRef.current?.focus(), 100);
    }
  }, [isOpen]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password === PORTFOLIO_ACCESS_PASSWORD) {
      setPassword('');
      setError('');
      onUnlocked();
      return;
    }
    setError('That access password is not recognised. Please try again.');
  };
  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };
  return <AnimatePresence data-id="element-153" data-mp="mmvh1ta3oq9g">
      {isOpen && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} role="dialog" aria-modal="true" aria-labelledby="portfolio-gate-title" data-id="element-154" data-mp="10k0kdlifjb10">
          <button type="button" className="absolute inset-0 bg-true-black/55 backdrop-blur-sm" aria-label="Close client access" onClick={handleClose} data-id="element-155" data-mp="28z8t9ea6ef0" />
          <motion.div initial={{
        opacity: 0,
        y: 18,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: 'easeOut'
      }} className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-dark-lighter p-7 shadow-2xl sm:p-10" data-id="element-156" data-mp="bzbu53tx6bm7">
            <button type="button" onClick={handleClose} className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-gold hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold" aria-label="Close client access" data-id="element-157" data-mp="2k6fa0v3v2djk">
              <X size={19} data-id="element-158" data-mp="3359r4aqsrq2w" />
            </button>

            <div className="mb-8 pr-10" data-id="element-159" data-mp="332xbi48ihrt0">
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10 text-gold" data-id="element-160" data-mp="2y5f3cg2bjgm2">
                <LockKeyhole size={25} data-id="element-161" data-mp="2t593iclwansw" />
              </div>
              <p className="mb-2 font-inter text-xs font-bold uppercase tracking-[0.2em] text-gold" data-id="element-162" data-mp="2p5rzlnm49ypu">
                Client access
              </p>
              <h2 id="portfolio-gate-title" className="font-syne text-3xl font-bold text-white sm:text-4xl" data-id="element-163" data-mp="1ht00b1d9flzu">
                How can we help today?
              </h2>
              <p className="mt-4 max-w-md font-inter leading-relaxed text-white/65" data-id="element-164" data-mp="31koqu3a2a841">
                Log a support ticket in the client portal, or enter your access
                password to view the professional profile and project archive.
              </p>
            </div>

            <div className="mb-8 rounded-xl border border-white/10 bg-dark p-5" data-id="element-165" data-mp="2wny8f9asb0fg">
              <div className="flex items-start gap-4" data-id="element-166" data-mp="3kekh92sm4oqi">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gold/10 text-gold" data-id="element-167" data-mp="3pc3txy9nwtgc">
                  <LifeBuoy size={20} data-id="element-168" data-mp="3bdciykq3jnwf" />
                </div>
                <div data-id="element-169" data-mp="3bexd498i14tr">
                  <h3 className="font-syne text-lg font-bold text-white" data-id="element-170" data-mp="3fdwg3oao6rik">
                    Byteweb support portal
                  </h3>
                  <p className="mt-1 font-inter text-sm leading-relaxed text-white/55" data-id="element-171" data-mp="1zwmlpr3ni6ey">
                    Existing clients can create tickets, log queries and track
                    progress on active work.
                  </p>
                </div>
              </div>
              <a ref={portalLinkRef} href={SUPPORT_PORTAL_URL} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gold px-5 py-3 font-inter text-sm font-bold text-gold transition-colors hover:bg-gold hover:text-true-white focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-dark" data-id="element-172" data-mp="1rh6s8as6g9p1">
                Open support portal
                <ExternalLink size={16} data-id="element-173" data-mp="1ngd1l9ncogsg" />
              </a>
            </div>

            <div className="mb-7 flex items-center gap-4" aria-hidden="true" data-id="element-174" data-mp="1in25o3k2tkfb">
              <span className="h-px flex-1 bg-white/10" data-id="element-175" data-mp="uwy4e2ud1wrh" />
              <span className="font-inter text-xs font-bold uppercase tracking-[0.16em] text-white/35" data-id="element-176" data-mp="pzxvzla1ghx7">
                Or
              </span>
              <span className="h-px flex-1 bg-white/10" data-id="element-177" data-mp="1fn1wy4byctoi" />
            </div>

            <form onSubmit={handleSubmit} noValidate data-id="element-178" data-mp="1jo3qc4j6ubik">
              <label htmlFor="portfolio-password" className="mb-2 block font-inter text-sm font-semibold text-white" data-id="element-179" data-mp="1ofvigjhzspat">
                Access password
              </label>
              <input ref={inputRef} id="portfolio-password" type="password" value={password} onChange={event => {
            setPassword(event.target.value);
            if (error) setError('');
          }} className="w-full rounded-xl border border-white/15 bg-dark px-4 py-3.5 font-inter text-white outline-none transition-colors placeholder:text-white/35 focus:border-gold focus:ring-2 focus:ring-gold/20" placeholder="Enter password" autoComplete="current-password" autoCapitalize="none" spellCheck={false} aria-invalid={Boolean(error)} aria-describedby={error ? 'portfolio-password-error' : undefined} data-id="element-180" data-mp="wp7ivbvyvrz9" />
              <div className="min-h-6 pt-2" aria-live="polite" data-id="element-181" data-mp="o4ft6l48b6r">
                {error && <p id="portfolio-password-error" className="font-inter text-sm text-red-400" data-id="element-182" data-mp="3rupbx4q129an">
                    {error}
                  </p>}
              </div>

              <button type="submit" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-true-black bg-gold px-5 py-3.5 font-inter font-bold text-true-white transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-dark-lighter" data-id="element-183" data-mp="3dw7jitp6g8me">
                View professional profile
                <ArrowRight size={18} data-id="element-184" data-mp="g1qlvlca61vy" />
              </button>
            </form>

            <div className="mt-7 flex items-start gap-3 border-t border-white/10 pt-5" data-id="element-185" data-mp="kz8oyv3m9mw1">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-gold" data-id="element-186" data-mp="79olto64inm7" />
              <p className="font-inter text-xs leading-relaxed text-white/45" data-id="element-187" data-mp="ba8m30lzu7ck">
                Profile access remains available for this browser session only.
                Not a client yet? Book a consultation and we’ll share the right
                next step.
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}